/**
 * Deux valeurs de clé OCPP sont la même si elles ne diffèrent que par des espaces, par l'écriture d'un booléen
 * (`true` / `True` / `TRUE` / `1` / `yes`…) ou par l'ordre d'une liste — comme côté serveur (`ConfigurationValue`).
 * `1` / `0` ne comptent comme booléens que face à un mot booléen : seuls, ce sont des nombres.
 */
const BOOLEANS = [['true', 'false'], ['1', '0'], ['yes', 'no'], ['on', 'off'], ['enabled', 'disabled']]

function format(value) {
  const text = String(value ?? '').trim().toLowerCase()
  const index = BOOLEANS.findIndex(pair => pair.includes(text))
  return index < 0 ? null : index
}

// Vrai, faux, ou null si ce n'est pas un booléen ; `numbers` : `1` / `0` en sont (la clé a des mots booléens).
function truth(value, numbers) {
  const index = format(value)
  if (index === null || (index === 1 && !numbers)) {
    return null
  }
  return String(value).trim().toLowerCase() === BOOLEANS[index][0]
}

function isWord(value) {
  return format(value) !== null && format(value) !== 1
}

function normalize(value) {
  const text = String(value ?? '').trim()
  if (text.includes(',')) {
    return text.split(',').map(item => item.trim()).sort().join(',')
  }
  return text
}

export default {
  same(a, b) {
    const wanted = truth(a, isWord(b))
    const found = truth(b, isWord(a))
    if (wanted !== null && found !== null) {
      return wanted === found
    }
    return normalize(a) === normalize(b)
  },

  // Une clé booléenne : toutes ses valeurs sont true / false ou 1 / 0, dont au moins un true / false. Pas On / Off,
  // Yes / No… : `Off` peut aller avec `Glow` (Wallbox LED Heart Beat Mode).
  isBoolean(values) {
    const present = values.filter(value => value !== null && value !== undefined)
    return present.length > 0 && present.every(value => [0, 1].includes(format(value))) && present.some(value => format(value) === 0)
  },

  // Dans une clé booléenne : vrai ou faux, quelle que soit l'écriture.
  truth(value) {
    return truth(value, true)
  },

  /**
   * La valeur voulue écrite comme la borne écrit ce booléen : d'après la valeur actuelle de la clé, sinon (vide) d'après
   * ses autres booléens (`fallback`, `True`, `1`…) ; sinon telle quelle. Un `1` voulu sur une clé vide reste un nombre.
   * Seuls `true` / `false` et `1` / `0` se convertissent : un `Off` peut aller avec `Glow`, jamais deviné en `On`.
   */
  inFormatOf(wanted, current, fallback = null) {
    let reference = current
    if (format(current) === null) {
      if (!isWord(wanted) || !fallback) {
        return wanted
      }
      reference = fallback
    }
    if (![0, 1].includes(format(reference)) || ![0, 1].includes(format(wanted))) {
      return wanted
    }
    const wantedTruth = truth(wanted, isWord(reference))
    const referenceTruth = truth(reference, isWord(wanted))
    if (wantedTruth === null || referenceTruth === null) {
      return wanted
    }
    const written = BOOLEANS[format(reference)][wantedTruth ? 0 : 1]
    const text = String(reference).trim()
    if (text === text.toUpperCase() && text !== text.toLowerCase()) {
      return written.toUpperCase()
    }
    if (text === text.charAt(0).toUpperCase() + text.slice(1).toLowerCase() && text !== text.toLowerCase()) {
      return written.charAt(0).toUpperCase() + written.slice(1)
    }
    return written
  },
}
